import {AttrsSchema, Component, ConfigOptions} from 'panel';

import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {SegmentFilterController, SegmentItem} from 'common/charts/contexts/segment-filter-controller';
import {ChartLegendTheme} from 'common/components/chart-legend/constants';
import {ColorChoice} from 'common/report/util/funnels/colors';
import {DateKeyedFunnelSegments, SelectMode, TrendChartType} from 'common/report/models/funnels/types';
import {ChartDisplayContext} from 'common/widgets/legend-line-chart';
import {FunnelsQuery} from 'common/report/models/funnels/funnels-query';
import {getLegendSections, getRenamedOrderedSegmentSteps} from 'common/report/models/funnels/utils';
import {orderSegmentsbyTotalConversions} from 'common/report/data/funnels/utils';
import {hasData} from 'common/report/util/funnels/utils';
import {ODA_FUNNELS_UPSELL_DESCRIPTION} from 'common/widgets/oda-control/constants';
import {LineChartAttrs} from 'common/charts/visualization/line-chart';
import {DisplayAnalysisType} from 'common/charts/visualization/line-chart/enums';
import {Section} from 'common/components/items-menu/types';
import {DEFAULT_SELECTED_SEGMENTS_LIMIT, MAX_SELECTED_SEGMENTS_LIMIT, ReportName} from 'common/report/constants';

import {formatTooltip, formatYAxisLabel, getChartData} from './formatters';
import template from './index.jade';
import css from './index.styl?inline';

interface ChartAttrs {
  'can-use-data-table': boolean;
  'display-context': ChartDisplayContext;
  'download-mode': boolean;
  'event-index': null | number;
  'show-oda-footer': boolean;
  'incomplete-from-date': boolean;
  'incomplete-to-date': boolean;
  'is-compact': boolean;
  'is-waiting-for-schedule-analysis': boolean;
  'legend-theme': ChartLegendTheme;
  'select-mode': SelectMode;
  selected: ColorChoice[];
  'show-on-demand-analysis-upsell': boolean;
  'show-annotations': boolean;
  timezone: string;
  'trend-type': TrendChartType;
  'utc-offset': number;
}
export class FunnelsConversionOverTimeChart extends Component<EmptyObj, ChartAttrs, any, any, ChartsContextRegistry> {
  private _segmentData: DateKeyedFunnelSegments[] = [];
  private _comparisonSegmentData: DateKeyedFunnelSegments[] = [];
  public funnelsQuery: FunnelsQuery;

  static get attrsSchema(): AttrsSchema<ChartAttrs> {
    return {
      'can-use-data-table': `boolean`,
      'display-context': {
        type: `string`,
        enum: Object.values(ChartDisplayContext),
        default: ChartDisplayContext.Primary,
      },
      'download-mode': `boolean`,
      'event-index': {type: `number`, default: null},
      'show-oda-footer': `boolean`,
      'incomplete-from-date': `boolean`,
      'incomplete-to-date': `boolean`,
      'is-compact': `boolean`,
      'is-waiting-for-schedule-analysis': `boolean`,
      'legend-theme': {type: `string`, enum: Object.values(ChartLegendTheme), default: ChartLegendTheme.Primary},
      'select-mode': {type: `string`, enum: Object.values(SelectMode), default: SelectMode.Position},
      selected: {type: `json`, default: []},
      'show-annotations': {
        type: `boolean`,
        description: `shows global annotations on mp-line-chart`,
      },
      'show-on-demand-analysis-upsell': `boolean`,
      timezone: `string`,
      'trend-type': {type: `string`, enum: Object.values(TrendChartType), default: TrendChartType.Conversion},
      'utc-offset': `number`,
    };
  }

  get config(): ConfigOptions<EmptyObj, any, ChartsContextRegistry> {
    return {
      css,
      contexts: [`segmentFilterController` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController({
          defaultNumSelections: DEFAULT_SELECTED_SEGMENTS_LIMIT[ReportName.Funnels],
          selectAllLimit: MAX_SELECTED_SEGMENTS_LIMIT[ReportName.Funnels],
        }),
      },
      template,
      useShadowDom: true,
      defaultState: {},
      helpers: {
        ODA_FUNNELS_UPSELL_DESCRIPTION,
        ChartLegendTheme,

        passThrough: (ev) => {
          ev.stopPropagation();
          this.dispatchEvent(new CustomEvent(ev.type, ev));
        },

        canUseDataTable: () => this.attr(`can-use-data-table`),

        getLineChartProps: () => {
          return {
            chartData: this.helpers.getChartData(),
            formatYAxisLabel: this.helpers.formatYAxisLabel,
            formatTooltip: this.helpers.formatTooltip,
          };
        },

        getLineChartAttrs: (): Partial<LineChartAttrs> => {
          return {
            'chart-width': null,
            'display-analysis-type': DisplayAnalysisType.Linear,
            'incomplete-from-date': this.attr(`incomplete-from-date`),
            'incomplete-to-date': this.attr(`incomplete-to-date`),
            'show-annotations': this.attr(`show-annotations`),
            timezone: this.attr(`timezone`),
            'utc-offset': this.attr(`utc-offset`),
            'zoom-type': `x`,
            'time-unit': this.helpers.getTrendUnit(),
          };
        },

        clickAnalyze: () => this.dispatchEvent(new CustomEvent(`clickAnalyze`)),

        hasNoData: () => !hasData(this.segmentData),

        getLegendSections: (): Section[] => {
          return getLegendSections(
            this.attr(`selected`),
            this.attr(`select-mode`),
            this.funnelsQuery,
            this.segmentData,
          );
        },

        getChartData: () =>
          getChartData({
            comparedTrends: this.comparisonSegmentData,
            eventIndex: this.attr(`event-index`),
            funnelsQuery: this.funnelsQuery,
            orderedTrends: this.segmentData,
            selected: this.attr(`selected`),
            selectMode: this.attr(`select-mode`),
            trendType: this.attr(`trend-type`),
          }),

        getTrendUnit: () => this.funnelsQuery.trendUnit,

        formatYAxisLabel: (value: number) => formatYAxisLabel(value, this.attr(`trend-type`)),

        formatTooltip: (x: number, _y: number, series: any, _idx: number) =>
          formatTooltip({
            comparedTrends: this.comparisonSegmentData,
            eventIndex: this.attr(`event-index`),
            funnelsQuery: this.funnelsQuery,
            isCompact: this.attr(`is-compact`),
            orderedTrends: this.segmentData,
            trendType: this.attr(`trend-type`),
            x,
            series,
          }),
      },
    };
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  public set segmentData(val: DateKeyedFunnelSegments[]) {
    this._segmentData = orderSegmentsbyTotalConversions(val);
  }

  public get segmentData(): DateKeyedFunnelSegments[] {
    return this._segmentData;
  }

  public set comparisonSegmentData(val: DateKeyedFunnelSegments[]) {
    this._comparisonSegmentData = orderSegmentsbyTotalConversions(val);
  }

  public get comparisonSegmentData(): DateKeyedFunnelSegments[] {
    return this._comparisonSegmentData;
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.attr(`can-use-data-table`)) {
      this.updateSegmentFilterController();
    }
  }

  private updateSegmentFilterController() {
    const selected = this.attr(`selected`);
    const segments = getRenamedOrderedSegmentSteps(this.funnelsQuery, this.segmentData);
    const keyToColorMap = new Map(selected.map((s) => [s.key, s.color]));
    const legendSegmentItems: SegmentItem[] = segments.map((segment) => {
      return {
        color: keyToColorMap.get(segment.prop),
        id: segment.prop,
        label: segment.propName,
      };
    });

    this.segmentFilterController.loadSections([{items: legendSegmentItems}]);
    this.segmentFilterController.loadPersistedSelections([this.attr(`selected`).map((s) => s.key)]);
  }
}
customElements.define(`mp-funnels-conversion-over-time-chart`, FunnelsConversionOverTimeChart);
